import {
    Bounds, Contour, Matrix, namespace, Point,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import CircleItem from './circleItem';
import CombinedGroupItem from './combinedGroupItem';
import ContainerItem from './containerItem';
import FigureItem from './figureItem';
import GripItem from './gripItem';
import GroupItem from './groupItem';
import RectangleItem from './rectangleItem';
import TextItem from './textItem';

namespace('Ofcs.Editor.Item');

/**
 * @class FoamItem
 * @extends ContainerItem
 */
export default window.Ofcs.Editor.Item.FoamItem = ContainerItem.extend({
    type: 'foam',

    defaults: {
        /**
         * @property {String} Type
         */
        type: null,

        /**
         * Current position (top left)
         * @property {Point} attributes.position
         */
        position: new Point(0, 0),

        /**
         * Width of the foam
         * @property {Number} attributes.width
         */
        width: 0,

        /**
         * Height of the foam
         * @property {Number} attributes.height
         */
        height: 0,

        /**
         * Depth of the foam
         * @property {Number} attributes.depth
         */
        depth: 9999,

        /**
         * Extension of the foam
         * @property {Number} attributes.extension
         */
        extension: 0,

        /**
         * Minimum depth of the foam
         * @property {Number} attributes.minDepth
         */
        minDepth: 0,

        /**
         * Safety distance of the foam
         * @property {Number} attributes.safetyDistance
         */
        safetyDistance: 0,

        /**
         * Safety distance bottom of the foam
         * @property {Number} attributes.safetyDistanceBottom
         */
        safetyDistanceBottom: 0,

        /**
         * Safety margins between items
         * @property {Array} attributes.safetyMargins
         */
        safetyMargins: [],

        /**
         * Thickness of the foam
         * @property {Number} attributes.thickness
         */
        thickness: 9999,

        /**
         * Path to layout image
         * @property {String} attributes.layoutImage
         */
        layoutImage: '/bundles/ofcseditor/img/foam-pattern-2d3539.jpg',

        /**
         * Path to background image
         * @property {String} attributes.backgroundImage
         */
        backgroundImage: '/bundles/ofcseditor/img/foam-pattern-0074c9.jpg',

        /**
         * JSON object for the sensor background image
         * @property {String} attributes.sensorBackground
         */
        sensorPath: [],

        /**
         * Color for the layout background
         * @cfg {string} [attributes.layoutColor=#2d3539]
         */
        layoutColor: '#2d3539',

        /**
         * Color for the bags background
         * @cfg {string} [attributes.bagColor=#0074c9]
         */
        bagColor: '#0074c9',

        /**
         * Note for this layout
         * @cfg {String} attributes.note
         */
        note: '',

        /**
         * Which label sizes are allowed in this layout
         * @cfg {Array} attributes.availableLabelSizes
         */
        availableLabelSizes: [],

        /**
         * Which label size is the default for this layout
         * @cfg {Number} attributes.defaultLabelSize
         */
        defaultLabelSize: 0,

        /**
         * Which label font family is the default for this layout
         * @cfg {string} attributes.defaultLabelFontFamily
         */
        defaultLabelFontFamily: null,
    },

    /**
     * Triggered when the segments are updated
     *
     * @event updatesegments
     * @param {FoamItem} item
     */

    supportsCopying: false,

    sensorContour: null,

    initialize() {
        ContainerItem.prototype.initialize.call(this);
        this.supportsCollisions = true;
        this.safetyMarginCache = {};

        if (this.get('sensorPath').length !== 0) {
            this.sensorContour = Contour.fromSegments(this.get('sensorPath'));
        }

        this.on('change', this.onItemChange, this);
    },

    /**
     * Is called when item attributes change
     * @param {FoamItem} item
     */
    onItemChange(item) {
        if (item.hasChangedProperty('width', 'height') && !item.hasChangedProperty('ext')) {
            const segments = this.calculateSegments();

            const safetySegments = this.calculateSegments();
            safetySegments.push(this.calculateSecuritySegments()[0]);

            const innerCollisionSegments = [this.collisionSegments()];
            innerCollisionSegments.push(this.calculateSecuritySegments()[0]);

            const outerCollisionSegments = [this.collisionSegments()];
            outerCollisionSegments.push(this.calculateSegments()[0]);

            this.set({
                ext: segments,
                orig: segments,
                safety: safetySegments,
                innerCollision: innerCollisionSegments,
                outerCollision: outerCollisionSegments,
            });
        }
    },

    /**
     * Get safety margin between items by item depth.
     *
     * @param {number} depth
     * @param {number} length
     *
     * @return {number}
     */
    getSafetyMarginByDepth(depth = 0, length = 0) {
        const lengths = [];
        let foundLength = null;
        const safetyMargins = this.get('safetyMargins');

        // if no safety margins are specified use foams site safety distance
        if (!safetyMargins || safetyMargins.length === 0) {
            return this.get('safetyDistance');
        }

        const cacheId = `${depth}#${length}`;
        if (this.safetyMarginCache[cacheId]) {
            return this.safetyMarginCache[cacheId];
        }

        // check all safety distances by depth
        // eslint-disable-next-line no-restricted-syntax,guard-for-in
        for (foundLength in safetyMargins) {
            lengths.push(parseInt(foundLength, 10));
        }

        for (let i = lengths.length - 1; i >= 0; i -= 1) {
            if (length >= lengths[i]) {
                foundLength = safetyMargins[lengths[i]];
                break;
            }
        }

        if (foundLength === null) {
            throw new Error('No matching length found');
        }

        // use smallest safety distance as fallback
        this.safetyMarginCache[cacheId] = foundLength[foundLength.length - 1].margin;

        // check all safety distances by depth
        for (let i = 0; i <= foundLength.length - 1; i += 1) {
            if (depth <= foundLength[i].depth) {
                this.safetyMarginCache[cacheId] = foundLength[i].margin;
                break;
            }
        }

        return this.safetyMarginCache[cacheId];
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getBounds() {
        return this.cache.get('bounds', () => {
            const { x, y } = this.getPosition();
            const cacheBounds = new Bounds(
                this.getPosition().clone(),
                new Point(x + this.get('width'), y + this.get('height')),
            );

            return cacheBounds;
        });
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getMarginBounds() {
        return this.getBounds();
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getInnerBounds() {
        return this.cache.get('innerBounds', () => {
            const safetyMargin = this.getSafetyMargin();
            const { x, y } = this.getPosition();
            const cacheBounds = new Bounds(
                new Point(x + safetyMargin, y + safetyMargin),
                new Point((x + this.get('width')) - safetyMargin, (y + this.get('height')) - safetyMargin),
            );

            return cacheBounds;
        });
    },

    /**
     * Return safety margin for this item
     *
     * @return {number}
     */
    getSafetyMargin() {
        return this.get('safetyDistance');
    },

    /**
     * Calculates path segements from item's
     * width and height
     * @return {Array}
     */
    calculateSegments() {
        const width = this.get('width');
        const height = this.get('height');
        const path = [];

        path.push(['M', 0, 0]);
        path.push(['L', width, 0]);
        path.push(['L', width, height]);
        path.push(['L', 0, height]);
        path.push(['L', 0, 0]);

        return [path];
    },

    /**
     * Calculates security path segments
     * from width, height and security margin
     * @return {Array}
     */
    calculateSecuritySegments() {
        const width = this.get('width');
        const height = this.get('height');
        const securityMargin = this.getSafetyMargin();
        const path = [];

        path.push(['M', securityMargin, securityMargin]);
        path.push(['L', securityMargin, height - securityMargin]);
        path.push(['L', width - securityMargin, height - securityMargin]);
        path.push(['L', width - securityMargin, securityMargin]);
        path.push(['L', securityMargin, securityMargin]);

        return [path];
    },

    /**
     * Calculates collision segments
     * @return {Array}
     */
    collisionSegments() {
        return [
            ['M', -1000, -1000],
            ['L', 1400, -1000],
            ['L', 1400, 1400],
            ['L', -1000, 1400],
            ['L', -1000, -1000],
        ];
    },

    /**
     * Get top-middle point respect rotation.
     *
     * @return {Point}
     */
    getPointTop() {
        const matrix = new Matrix();
        const { x, y } = this.get('position');
        const width = this.get('width');
        const point = new Point(x + (width / 2), y);

        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-middle point respect rotation.
     *
     * @return {Point}
     */
    getPointBottom() {
        const matrix = new Matrix();
        const { x, y } = this.get('position');
        const height = this.get('height');
        const width = this.get('width');
        const point = new Point(x + (width / 2), y + height);

        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-left point respect rotation.
     *
     * @return {Point}
     */
    getPointLeft() {
        const matrix = new Matrix();
        const { x, y } = this.get('position');
        const height = this.get('height');
        const point = new Point(x, y + (height / 2));

        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-right point respect rotation.
     *
     * @return {Point}
     */
    getPointRight() {
        const matrix = new Matrix();
        const { x, y } = this.get('position');
        const height = this.get('height');
        const width = this.get('width');
        const point = new Point(x + width, y + (height / 2));

        point.transform(matrix);

        return point;
    },

    /**
     * Get top-left point respect rotation.
     *
     * @return {Point}
     */
    getPointTopLeft() {
        const matrix = new Matrix();
        const point = this.get('position').clone();

        point.transform(matrix);

        return point;
    },

    /**
     * Get top-right point respect rotation.
     *
     * @return {Point}
     */
    getPointTopRight() {
        const matrix = new Matrix();
        const { x, y } = this.get('position');
        const width = this.get('width');
        const point = new Point(x + width, y);

        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-left point respect rotation.
     *
     * @return {Point}
     */
    getPointBottomLeft() {
        const matrix = new Matrix();
        const { x, y } = this.get('position');
        const height = this.get('height');
        const point = new Point(x, y + height);

        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-right point respect rotation.
     *
     * @return {Point}
     */
    getPointBottomRight() {
        const matrix = new Matrix();
        const { x, y } = this.get('position');
        const width = this.get('width');
        const height = this.get('height');
        const point = new Point(x + width, y + height);

        point.transform(matrix);

        return point;
    },

    /**
     * Initializes item handles
     *
     * @param {Ofcs.Editor.Svg.Canvas} canvas - svg canvas instance
     * @return {Ofcs.Editor.Svg.Handle.ResizeRectangleHandle}
     */
    initializeHandles(canvas) {
        return new Ofcs.Editor.Svg.Handle.ResizeFoamHandle(this, canvas);
    },

    /**
     * Clear cache on change.
     *
     * @param {Object} changes
     */
    clearCache(changes) {
        if (!this.cache) return;

        ContainerItem.prototype.clearCache.call(this, changes);
        const watchedProperties = ['width', 'height', 'rotation', 'flipped', 'position', 'depth'];

        if (Object.keys(changes).some(key => watchedProperties.indexOf(key) >= 0)) {
            this.cache.remove('innerBounds');
        }
    },

    /**
     * @param {AbstractItem} item
     * @return {boolean}
     */
    isItemSupported(item) {
        return item instanceof CircleItem
            || item instanceof RectangleItem
            || item instanceof GripItem
            || item instanceof FigureItem
            || item instanceof GroupItem
            || item instanceof CombinedGroupItem
            || item instanceof TextItem;
    },
});
